import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Grid3X3, BookOpen, Shield, Lock, Eye, Server, Network, ShieldCheck, ChevronDown, ChevronUp, Download, Globe } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuthStore } from '../services/auth';
import { organizationsApi, authApi, camerasApi, analyticsApi } from '../services/api';
import { useTutorial } from '../App';

function SecurityFeatures() {
  const [showNetwork, setShowNetwork] = useState(false);

  const features = [
    {
      icon: <Eye className="w-5 h-5 text-green-600 dark:text-green-400" />,
      bg: 'bg-green-100 dark:bg-green-900/30',
      title: 'Privacy by Design',
      description: 'All face detection and AI processing happens on-device. No raw camera images are ever transmitted or stored on the server.',
    },
    {
      icon: <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400" />,
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      title: 'Encryption in Transit',
      description: 'All external traffic is encrypted via TLS 1.2+ (HTTPS/WSS) with auto-renewed Let\'s Encrypt certificates.',
    },
    {
      icon: <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />,
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      title: 'Authentication & Access Control',
      description: 'JWT authentication with short-lived tokens, Argon2 password hashing, role-based access control, and multi-tenant data isolation.',
    },
    {
      icon: <Server className="w-5 h-5 text-orange-600 dark:text-orange-400" />,
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      title: 'Data Sovereignty',
      description: 'No external cloud dependencies and no telemetry. All data stays within MN Digital\'s managed infrastructure \u2014 nothing is sent to third-party services.',
    },
    {
      icon: <Shield className="w-5 h-5 text-red-600 dark:text-red-400" />,
      bg: 'bg-red-100 dark:bg-red-900/30',
      title: 'Rate Limiting & Hardening',
      description: 'Auth endpoints are rate-limited against brute-force attacks. Containers run as non-root users with network isolation.',
    },
  ];

  const networkRules = [
    { direction: 'Inbound', source: 'Dashboard users', protocol: 'HTTPS / WSS', port: '443', purpose: 'Web dashboard & real-time updates' },
    { direction: 'Inbound', source: 'Camera devices', protocol: 'HTTPS / WSS', port: '443', purpose: 'Heartbeat, analytics, snapshots' },
    { direction: 'Inbound', source: 'Media players', protocol: 'HTTPS / WSS', port: '443', purpose: 'Config, triggers, video download' },
    { direction: 'Outbound', source: 'Server', protocol: 'HTTPS', port: '443', purpose: 'Certificate renewal (Let\'s Encrypt)' },
  ];

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Shield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Security Features</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400">Platform security overview and network requirements</p>
          </div>
        </div>
        <a
          href="/SECURITY.pdf"
          download="Dominate_Insights_Security.pdf"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/40 rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </a>
      </div>

      <div className="space-y-3">
        {features.map((feature) => (
          <div key={feature.title} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-slate-900 rounded-lg">
            <div className={`p-2 ${feature.bg} rounded-lg shrink-0`}>
              {feature.icon}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">{feature.title}</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">{feature.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Network Requirements */}
      <div className="mt-6">
        <button
          onClick={() => setShowNetwork(!showNetwork)}
          className="flex items-center gap-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 cursor-pointer"
        >
          <Network className="w-4 h-4" />
          Network Requirements (Firewall Rules)
          {showNetwork ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showNetwork && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-slate-700">
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-slate-300">Direction</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-slate-300">Source</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-slate-300">Protocol</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-slate-300">Port</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-slate-300">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {networkRules.map((rule, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-slate-800">
                    <td className="py-2 px-3 text-gray-900 dark:text-white">{rule.direction}</td>
                    <td className="py-2 px-3 text-gray-700 dark:text-slate-300">{rule.source}</td>
                    <td className="py-2 px-3">
                      <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                        {rule.protocol}
                      </span>
                    </td>
                    <td className="py-2 px-3 font-mono text-gray-900 dark:text-white">{rule.port}</td>
                    <td className="py-2 px-3 text-gray-700 dark:text-slate-300">{rule.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-xs text-gray-500 dark:text-slate-400">
              All external traffic uses port 443 (HTTPS) only. Internal services (PostgreSQL, Redis) are not exposed to the internet.
              Camera devices can operate offline and buffer data locally for up to 30 days.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Settings() {
  const { user, organizationId, setOrganization, refreshToken, setAuth } = useAuthStore();
  const [orgName, setOrgName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [zoneMonitoringEnabled, setZoneMonitoringEnabled] = useState(false);
  const queryClient = useQueryClient();
  const tutorial = useTutorial();

  // Fetch cameras to get their IDs for clearing data
  const { data: cameras } = useQuery({
    queryKey: ['cameras'],
    queryFn: camerasApi.list,
  });

  // Fetch organization settings
  const { data: organization } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => organizationsApi.get(organizationId!),
    enabled: !!organizationId,
  });

  // Sync toggle state with organization data
  useEffect(() => {
    if (organization) {
      setZoneMonitoringEnabled(organization.zone_monitoring_enabled);
    }
  }, [organization]);

  const createOrgMutation = useMutation({
    mutationFn: (name: string) => organizationsApi.create(name),
    onSuccess: async (org) => {
      setOrganization(org.id);
      setSuccess('Organization created! Refreshing session...');
      setError(null);
      // Refresh the auth token to include the new org_id
      if (refreshToken) {
        try {
          const newAuth = await authApi.refresh(refreshToken);
          setAuth(newAuth);
          setSuccess('Organization created successfully! You can now register cameras.');
        } catch {
          setSuccess('Organization created! Please log out and back in for full access.');
        }
      }
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to create organization');
      setSuccess(null);
    },
  });

  const handleCreateOrg = (e: React.FormEvent) => {
    e.preventDefault();
    if (orgName.trim()) {
      createOrgMutation.mutate(orgName.trim());
    }
  };

  const clearDataMutation = useMutation({
    mutationFn: (cameraIds: string[]) => analyticsApi.clearData(cameraIds),
    onSuccess: (result) => {
      setSuccess(`Cleared ${result.deleted} detection events from the server.`);
      setError(null);
      setShowClearConfirm(false);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to clear data');
      setSuccess(null);
    },
  });

  const handleClearData = () => {
    if (cameras && cameras.length > 0) {
      const cameraIds = cameras.map((c) => c.id);
      clearDataMutation.mutate(cameraIds);
    } else {
      setError('No cameras found to clear data for');
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-500 dark:text-slate-400">Manage your account and organization settings</p>
      </div>

      {/* Getting Started */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Getting Started Guide</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Learn how to use HumanVu</p>
            </div>
          </div>
          <Button variant="outline" onClick={tutorial.open}>
            Show Tutorial
          </Button>
        </div>
      </Card>

      {/* Profile Settings */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Profile</h2>
        <div className="space-y-4">
          <Input label="Email" value={user?.email || ''} disabled />
          <Input
            label="Role"
            value={user?.role || ''}
            disabled
            className="capitalize"
          />
          <div className="pt-4">
            <Button variant="outline">Change Password</Button>
          </div>
        </div>
      </Card>

      {/* Organization Settings */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Organization</h2>
        {!organizationId ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 dark:bg-amber-900/20 border border-yellow-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-amber-300">
                You need to create an organization before you can register cameras.
              </p>
            </div>
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <Input
                label="Organization Name"
                placeholder="Enter your organization name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
              />
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
              {success && (
                <p className="text-sm text-green-600">{success}</p>
              )}
              <div className="pt-4">
                <Button type="submit" isLoading={createOrgMutation.isPending}>
                  Create Organization
                </Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-sm text-green-800 dark:text-green-300">
                Organization configured. You can register cameras from the Cameras page.
              </p>
            </div>
            <Input label="Organization ID" value={organizationId} disabled />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                <div className="flex items-center gap-1.5">
                  <Globe className="w-4 h-4" />
                  Timezone
                </div>
              </label>
              <select
                value={organization?.timezone || 'UTC'}
                onChange={(e) => {
                  organizationsApi.updateSettings(organizationId!, { timezone: e.target.value })
                    .then(() => {
                      queryClient.invalidateQueries({ queryKey: ['organization'] });
                      setSuccess('Timezone updated successfully.');
                      setError(null);
                    })
                    .catch((err) => {
                      setError(err.message || 'Failed to update timezone');
                      setSuccess(null);
                    });
                }}
                className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="UTC">UTC (GMT+0)</option>
                <option value="Africa/Johannesburg">Africa/Johannesburg (SAST, GMT+2)</option>
                <option value="Africa/Cairo">Africa/Cairo (EET, GMT+2)</option>
                <option value="Africa/Lagos">Africa/Lagos (WAT, GMT+1)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (EAT, GMT+3)</option>
                <option value="Europe/London">Europe/London (GMT/BST)</option>
                <option value="Europe/Paris">Europe/Paris (CET, GMT+1)</option>
                <option value="Europe/Berlin">Europe/Berlin (CET, GMT+1)</option>
                <option value="Europe/Moscow">Europe/Moscow (MSK, GMT+3)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST, GMT+4)</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST, GMT+5:30)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT, GMT+8)</option>
                <option value="Asia/Tokyo">Asia/Tokyo (JST, GMT+9)</option>
                <option value="Australia/Sydney">Australia/Sydney (AEST, GMT+10)</option>
                <option value="America/New_York">America/New_York (EST, GMT-5)</option>
                <option value="America/Chicago">America/Chicago (CST, GMT-6)</option>
                <option value="America/Denver">America/Denver (MST, GMT-7)</option>
                <option value="America/Los_Angeles">America/Los_Angeles (PST, GMT-8)</option>
                <option value="America/Sao_Paulo">America/Sao_Paulo (BRT, GMT-3)</option>
                <option value="Pacific/Auckland">Pacific/Auckland (NZST, GMT+12)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                All dashboard times will be displayed in this timezone.
              </p>
            </div>
          </div>
        )}
      </Card>

      {/* Zone Monitoring Settings — POC feature, only visible to steven@mnsa.co.za */}
      {organizationId && user?.email === 'steven@mnsa.co.za' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Zone Monitoring</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            Enable zone monitoring to track item counts in defined areas across cameras.
          </p>

          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-900 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Grid3X3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Zone Monitoring</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {zoneMonitoringEnabled
                    ? 'Enabled - Item counts are tracked in defined zones'
                    : 'Disabled - No zone data is collected'}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                organizationsApi.updateSettings(organizationId!, { zone_monitoring_enabled: !zoneMonitoringEnabled })
                  .then(() => {
                    queryClient.invalidateQueries({ queryKey: ['organization'] });
                    setSuccess('Zone monitoring settings updated successfully.');
                    setError(null);
                  })
                  .catch((err) => {
                    setError(err.message || 'Failed to update settings');
                    setSuccess(null);
                  });
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                zoneMonitoringEnabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-slate-600'
              } cursor-pointer`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  zoneMonitoringEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-2">
            No personal data is collected. Uses object detection to count items in zones.
          </p>
        </Card>
      )}

      {/* Data Retention — super_admin only */}
      {user?.role === 'super_admin' && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Data Retention</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
            Configure how long analytics data is stored before being automatically deleted.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-900 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Detection Events</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">Individual detection records</p>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-slate-200">90 days</span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-900 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Hourly Aggregates</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">Aggregated statistics</p>
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-slate-200">1 year</span>
            </div>
          </div>
        </Card>
      )}

      {/* Security Features */}
      <SecurityFeatures />

      {/* Danger Zone — super_admin only */}
      {user?.role === 'super_admin' && <Card className="border-red-200 dark:border-red-800">
        <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">Danger Zone</h2>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
          Beta testing: Clear all detection data to start fresh with improved detection settings.
        </p>
        {(error || success) && (
          <div className={`mb-4 p-3 rounded-lg ${error ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}>
            {error || success}
          </div>
        )}
        <div className="space-y-4">
          {!showClearConfirm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Clear All Detection Data</p>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Permanently delete all analytics data for all cameras ({cameras?.length || 0} cameras)
                </p>
              </div>
              <Button variant="danger" onClick={() => setShowClearConfirm(true)}>
                Clear Data
              </Button>
            </div>
          ) : (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg space-y-4">
              <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                Are you sure? This will permanently delete all detection events from the server.
              </p>
              <p className="text-xs text-red-600 dark:text-red-400">
                Note: This only clears server data. Client machines may still have local unsynced data.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="danger"
                  onClick={handleClearData}
                  isLoading={clearDataMutation.isPending}
                >
                  Yes, Clear All Data
                </Button>
                <Button variant="outline" onClick={() => setShowClearConfirm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>}
    </div>
  );
}
